import {RootStore} from "./rootStore";
import {action, observable, computed, makeObservable, runInAction} from "mobx";
import { ICoCurricularCard } from "../models/cocurricular";
import CardsStore from "./cardsStore";

export default class CoCurricularStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      ccCards: observable,
      ccDays: observable,
      ccTerms: observable,
      ccSubjectAreas: observable,
      ccForms: observable,
      ccImages: observable,

      ccChosenDays: observable,
      ccChosenTerms: observable,
      ccChosenSubjectAreas: observable,
      ccChosenForms: observable,

      ccCardChosen: observable,

      ccShowFilter: observable,

      ccSearch: observable,
  
      ccSetShowFilter: action,

      ccSetChosenDay: action,
      ccSetChosenTerm: action,
      ccSetChosenSubjectArea: action,
      ccSetChosenForm: action,

      ccSetCards: action,
      ccSetForms: action, 
      ccSetSubjectAreas: action,
      ccSetSchoolTerms: action,
      ccSetSearch: action,
      ccSetCardChosen: action,
      ccSetImages: action,
      ccImage: action,

      filteredCards: computed,

      ccFilteredTitles: computed,
    });
  }

  daysOfWeekOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];


  ccCards:ICoCurricularCard[] = [];
  ccDays:string[] = [];
  ccTerms:string[] = [];
  ccSubjectAreas:string[] = [];
  ccForms: string[] = [];
  ccImages: string[] = [];


  ccSearch: string = "";

  ccCardChosen: ICoCurricularCard|null = null;

  ccChosenDays:string[] = [];
  ccChosenTerms:string[] = [];
  ccChosenSubjectAreas:string[] = [];
  ccChosenForms:string[] = [];


  ccShowFilter: string|null = null;

  ccImageNumber: number = 0;

  ccSetChosenDay = (dayToAddOrRemove: string) => {
    this.ccChosenDays = this.ccChosenDays.includes(dayToAddOrRemove)
      ? this.ccChosenDays.filter(n => n !== dayToAddOrRemove)
      : [...this.ccChosenDays, dayToAddOrRemove];
  }

  ccSetChosenTerm = (termToAddOrRemove: string) => {
    this.ccChosenTerms = this.ccChosenTerms.includes(termToAddOrRemove)
      ? this.ccChosenTerms.filter(n => n !== termToAddOrRemove)
      : [...this.ccChosenTerms, termToAddOrRemove];
  }

  ccSetChosenSubjectArea = (subjectAreaToAddOrRemove: string) => {
    this.ccChosenSubjectAreas = this.ccChosenSubjectAreas.includes(subjectAreaToAddOrRemove)
      ? this.ccChosenSubjectAreas.filter(n => n !== subjectAreaToAddOrRemove)
      : [...this.ccChosenSubjectAreas, subjectAreaToAddOrRemove];
  }

  ccSetChosenForm = (formToAddOrRemove: string) => {
    this.ccChosenForms = this.ccChosenForms.includes(formToAddOrRemove)
      ? this.ccChosenForms.filter(n => n !== formToAddOrRemove)
      : [...this.ccChosenForms, formToAddOrRemove];
  }


  ccSetShowFilter = (open: string|null = null) => {
    if(open === null || open === this.ccShowFilter) {
      this.ccShowFilter = null;
      return;
    }

    this.ccShowFilter = open;
  }


  ccSetSearch = (search: string) => {
    this.ccSearch = search;
  }

  ccSetCardChosen = (card: ICoCurricularCard) => {
    this.ccCardChosen = card;
  }


  getDayIndex = day => this.daysOfWeekOrder.indexOf(day.toLowerCase());

  ccSetCards = (cards: ICoCurricularCard[]) => {
    // Load in the cards
    this.ccCards = cards;

    // Get all the days
    this.ccDays = cards
      .flatMap(card => card.coCurricular.days.nodes) // flatten the daySlots array
      .map(slot => slot.name) // extract the day from each slot
      .filter((day, index, self) => self.indexOf(day) === index)
      .sort((a, b) => this.getDayIndex(a) - this.getDayIndex(b));

      //console.log(cards);


    // // Get all the times
    // this.ccTerms = cards
    //   .flatMap(card => card.coCurricular.daySlots) // flatten the daySlots array
    //   .map(slot => slot.time) // extract the time from each slot
    //   .filter((time, index, self) => self.indexOf(time) === index)
    //   .sort((a, b) => {
    //     const [aHour, aMinute] = a.split(':').map(Number);
    //     const [bHour, bMinute] = b.split(':').map(Number);
    //     return aHour - bHour || aMinute - bMinute;
    //   });
  }

  // Pull in the forms from the taxonomy as it has the correct order
  ccSetForms = (forms) => {
    this.ccForms = forms.map(form => form.name).reverse();
  }

  ccSetSubjectAreas = (subjectAreas) => {
    this.ccSubjectAreas = subjectAreas.map(subjectArea => subjectArea.name).reverse();
  }


  ccSetSchoolTerms = (terms) => {
    this.ccTerms = terms.map(term => term.name);
  }

  ccSetImages = (images) => {
    images.sort(() => Math.random() - 0.5);

    this.ccImages = images.map(image => image.mediaItemUrl);
  }


  getFilteredCards = (): ICoCurricularCard[] => {
    return this.ccCards
      .filter(card => {
        const dayMatch =
          this.ccChosenDays.length === 0 ||
          card.coCurricular.days?.nodes?.some(slot => this.ccChosenDays.includes(slot.name));

        const subjectAreaMatch =
          this.ccChosenSubjectAreas.length === 0 ||
          card.coCurricular.coCurricularSubjectArea?.nodes?.some(slot =>
            this.ccChosenSubjectAreas.includes(slot.name)
          );

        const formsMatch =
          this.ccChosenForms.length === 0 ||
          card.coCurricular.form?.nodes?.some(form => this.ccChosenForms.includes(form.name));

        const termMatch =
          this.ccChosenTerms.length === 0 ||
          card.coCurricular.term?.nodes?.some(term => this.ccChosenTerms.includes(term.name));

        return dayMatch && subjectAreaMatch && formsMatch && termMatch;
      })
      .sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }));
  };

  ccImage = () => {
    const image = this.ccImages[this.ccImageNumber];

    this.ccImageNumber = (this.ccImageNumber + 1) % this.ccImages.length;

    return image; 
  }

  get filteredCards():ICoCurricularCard[] {

    return this.getFilteredCards().filter(card => card.title.toLowerCase().includes(this.ccSearch.toLowerCase()))
      
  }

  get ccFilteredTitles(): string[] {
    return this.getFilteredCards()
      .map(card => card.title);
  }

}